import { z } from 'zod';

// ============================================
// Authentication & Authorization
// ============================================

export const UserRoleSchema = z.enum([
  'system_admin', // プラットフォーム運用担当
  'univ_admin', // 大学管理者
  'dept_staff', // 部門担当者
  'viewer', // 閲覧者
  'accountant', // 経理担当者
  'applicant', // 受験生（共通アカウント）
]);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const UserStatusSchema = z.enum(['active', 'suspended', 'deleted']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const UserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1),
  role: UserRoleSchema,
  departmentCode: z.string().nullable(), // 部門制限（dept_staffのみ）
  tenantId: z.string().nullable(), // 所属テナント（Univ Admin等）
  vendorId: z.string().nullable().optional(), // 所属ベンダー（System Admin等）
  status: UserStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type User = z.infer<typeof UserSchema>;

export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const AuthResponseSchema = z.object({
  user: UserSchema,
  accessToken: z.string(),
  expiresAt: z.string().datetime(),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

// ============================================
// Organization Types
// ============================================

// Enums
export const OrganizationTypeSchema = z.enum([
  'UNIVERSITY',
  'CAMPUS', // Added
  'GRADUATE_SCHOOL',
  'JUNIOR_COLLEGE',
  'VOCATIONAL_SCHOOL',
  'FACULTY',
  'DEPARTMENT',
  'COURSE',
  'MAJOR', // 専攻
  'CLASS', // 学級/講座
]);
export const ORGANIZATION_TYPES = OrganizationTypeSchema.options;
export type OrganizationType = z.infer<typeof OrganizationTypeSchema>;

export const OrganizationStatusSchema = z.enum([
  'ACTIVE', // 活動中
  'STOPPED_RECRUITMENT', // 募集停止（在校生あり・廃止予定）
  'ABOLISHED_SCHEDULED', // 廃止予定（募集停止とほぼ同義だが、廃止日が確定している）
  'ABOLISHED', // 廃止済み
]);
export type OrganizationStatus = z.infer<typeof OrganizationStatusSchema>;

export const ApprovalStatusSchema = z.enum(['DRAFT', 'PENDING', 'APPROVED', 'REJECTED']);
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;

// Core Domain Models

/**
 * Organization Structure (Identity)
 * Persistent container ID that survives renames.
 */
export const OrganizationStructureSchema = z.object({
  id: z.string().uuid(),
  type: OrganizationTypeSchema,
  createdAt: z.string().datetime(),
});
export type OrganizationStructure = z.infer<typeof OrganizationStructureSchema>;

/**
 * Organization Definition (State at a specific time)
 */
export const OrganizationDefinitionSchema = z.object({
  id: z.string().uuid(),
  organizationStructureId: z.string().uuid(),
  parentStructureId: z.string().uuid().nullable(), // Null for Root (University)

  // Basic Info
  code: z.string().min(1),
  nameFormal: z.string().min(1), // 正式名称（学生向け）
  nameShort: z.string().min(1), // 短縮名称（管理用）
  nameKana: z.string().nullable(), // 名称カナ（検索・ソート用）
  nameEn: z.string().nullable().optional(), // 英語名称
  displayOrder: z.number().int().default(0), // 表示順
  departmentCode: z.string().nullable(), // 部門コード（権限制御）

  // Validity Period
  validFrom: z.string().date(), // YYYY-MM-DD
  validTo: z.string().date().nullable(), // NULL = Indefinite (until next change)

  // Status & Lifecycle
  status: OrganizationStatusSchema,
  abolitionDate: z.string().date().nullable(), // If status is STOPPED/ABOLISHED_SCHEDULED
  successorId: z.string().uuid().nullable(), // 統廃合先（廃止時の引継ぎ先）
  changeReason: z.string().nullable(), // 変更理由（監査証跡）

  // Approval (For scheduled changes)
  approvalStatus: ApprovalStatusSchema,
  approvedBy: z.string().nullable(),
  approvedAt: z.string().datetime().nullable(),
});
export type OrganizationDefinition = z.infer<typeof OrganizationDefinitionSchema>;

/**
 * Organization Tree Node (For UI)
 */
export type OrganizationNode = OrganizationStructure & {
  currentDefinition: OrganizationDefinition; // The definition valid for the selected context date
  children: OrganizationNode[];
  pendingChanges?: ChangeRequest[]; // Scheduled but unapproved changes
};

/**
 * Change Request Types
 */
export const ChangeTypeSchema = z.enum([
  'RENAME', // 名称変更
  'MERGE', // 統合（複数組織を1つに）
  'ABOLISH', // 廃止
  'MOVE', // 親変更（階層移動）
]);
export type ChangeType = z.infer<typeof ChangeTypeSchema>;

export const ChangeRequestSchema = z.object({
  id: z.string().uuid(),
  targetStructureId: z.string().uuid(), // 対象組織
  changeType: ChangeTypeSchema,
  scheduledDate: z.string().date(), // 適用予定日

  // Proposed Changes (varies by changeType)
  proposedNameFormal: z.string().nullable(), // RENAME
  proposedNameShort: z.string().nullable(), // RENAME
  proposedParentId: z.string().uuid().nullable(), // MOVE
  successorId: z.string().uuid().nullable(), // MERGE/ABOLISH: 引継ぎ先

  reason: z.string().min(1), // 申請理由（必須）

  // Request Metadata
  requestedBy: z.string(),
  requestedAt: z.string().datetime(),

  // Approval
  approvalStatus: ApprovalStatusSchema,
  approvedBy: z.string().nullable(),
  approvedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),
});
export type ChangeRequest = z.infer<typeof ChangeRequestSchema>;

/**
 * Notification Types
 */
export const NotificationTypeSchema = z.enum([
  'NEW_ORGANIZATION', // 学部新設など
  'STOP_RECRUITMENT', // 募集停止
  'ABOLISH_ORGANIZATION', // 廃止
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

// Using a generic payload for now, can be refined with discriminated unions if needed.
export const NotificationSchema = z.object({
  id: z.string().uuid(),
  type: NotificationTypeSchema,
  targetOrganizationId: z.string().uuid().nullable(), // Null for new orgs
  payload: z.record(z.any()), // JSON object for flexible data

  status: ApprovalStatusSchema,

  isSubmitted: z.boolean().default(false),
  forceApply: z.boolean().default(false),

  reason: z.string().min(1),

  requestedBy: z.string(),
  requestedAt: z.string().datetime(),

  approvedBy: z.string().nullable(),
  approvedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),
});
export type Notification = z.infer<typeof NotificationSchema>;

/**
 * Entrance Exam Configuration
 */
/**
 * Entrance Exam Configuration
 */
export const ExamTypeSchema = z.enum([
  'GENERAL',
  'RECOMMENDATION',
  'AO',
  'SPECIAL',
  'TRANSFER',
  'GRADUATE',
  'INTERNATIONAL',
  'RETURN',
  'ADULT',
  'COMMON_TEST',
]);
export type ExamType = z.infer<typeof ExamTypeSchema>;

export const ExamCategorySchema = z.enum(['MAIN', 'MAKEUP', 'RETAKE']);
export type ExamCategory = z.infer<typeof ExamCategorySchema>;

export const ExamConfigSchema = z.object({
  concurrentRule: z
    .object({
      allowCrossExam: z.boolean(),
      allowCrossFaculty: z.boolean(),
      maxChoices: z.number().optional(),
      hasPriorityRanking: z.boolean(),
    })
    .optional(),
  subjectSelection: z
    .object({
      required: z.boolean(),
      ruleCode: z.string().optional(),
    })
    .optional(),
  dateSelection: z
    .object({
      type: z.enum(['FIXED', 'SELECTABLE']),
    })
    .optional(),
  venueSelection: z
    .object({
      type: z.enum(['FIXED', 'SELECTABLE', 'AUTO']),
    })
    .optional(),
  discountRuleCode: z.string().optional(),
  ageCalculation: z.enum(['AT_REGISTRATION', 'AS_OF_NEXT_APRIL_1']),
  requireQuestionnaire: z.boolean(),
  supplementalProcedures: z
    .array(
      z.object({
        roundLabel: z.string(),
        announcementDate: z.string(),
        procedureDeadline: z.string(),
        methodInfo: z.string().optional(),
      }),
    )
    .optional(),
});
export type ExamConfig = z.infer<typeof ExamConfigSchema>;

export const EntranceExamScheduleSchema = z.object({
  id: z.string().uuid(),
  academicYear: z.number().int(),
  name: z.string().min(1),
  slug: z.string().min(1),
  examType: ExamTypeSchema,
  category: ExamCategorySchema,
  parentScheduleId: z.string().uuid().nullable().optional(),

  // Dates (Aggregated/Representative)
  applicationStart: z.string(),
  applicationEnd: z.string(),
  paymentDeadline: z.string().nullable().optional(),
  announcementDate: z.string().nullable().optional(),
  procedureDeadline: z.string().nullable().optional(),

  exclusiveGroupId: z.string().nullable().optional(),
  config: ExamConfigSchema.optional().nullable(),
  isActive: z.boolean().default(true),

  // Relations (Projected)
  dates: z.array(z.any()).optional(), // Detailed dates
});
export type EntranceExamSchedule = z.infer<typeof EntranceExamScheduleSchema>;

export const EntranceExamDateSchema = z.object({
  id: z.string().uuid(),
  scheduleId: z.string().uuid(),
  date: z.string(),
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  capacity: z.number().nullable().optional(),
});
export type EntranceExamDate = z.infer<typeof EntranceExamDateSchema>;

export const EntranceExamOfferingSchema = z.object({
  id: z.string().uuid(),
  scheduleId: z.string().uuid(),
  structureId: z.string().uuid(), // Structure this offering belongs to
  capacity: z.number().int().nonnegative(),
  feeProductId: z.string().uuid().nullable().optional(),
  interviewRequired: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type EntranceExamOffering = z.infer<typeof EntranceExamOfferingSchema>;

/**
 * Venues - Exam locations
 */
export const VenueSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1),
  name: z.string().min(1),
  address: z.string().nullable().optional(),
  mapUrl: z.string().url().nullable().optional(),
  capacity: z.number().int().nullable().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Venue = z.infer<typeof VenueSchema>;

/**
 * Fees - Exam Fee, Admission Fee, etc.
 */
export const FeeSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1),
  name: z.string().min(1),
  price: z.number().int().nonnegative(),
  taxRate: z.number().default(0),
  validFrom: z.string().date().nullable().optional(),
  validTo: z.string().date().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Fee = z.infer<typeof FeeSchema>;

/**
 * Applications
 */
export const ApplicationStatusSchema = z.enum([
  'DRAFT',
  'SUBMITTED',
  'PAYMENT_PENDING',
  'PAID',
  'CONFIRMED',
  'EXAM_COMPLETE',
  'PASSED',
  'FAILED',
  'WAITLISTED',
  'ENROLLED',
  'DECLINED',
  'CANCELLED',
]);
export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>;

export const ApplicationSchema = z.object({
  id: z.string().uuid(),
  applicantId: z.string().uuid(),
  offeringId: z.string().uuid(),
  receiptNumber: z.string().nullable().optional(),
  examNumber: z.string().nullable().optional(),
  venueId: z.string().uuid().nullable().optional(),
  status: ApplicationStatusSchema,
  submittedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Application = z.infer<typeof ApplicationSchema>;

/**
 * Payments
 */
export const PaymentMethodSchema = z.enum(['CREDIT', 'CONVENIENCE', 'BANK_TRANSFER', 'PAY_EASY', 'FLYWIRE']);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const PaymentStatusSchema = z.enum(['PENDING', 'PROCESSING', 'PAID', 'FAILED', 'REFUNDED', 'CANCELLED']);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const PaymentSchema = z.object({
  id: z.string().uuid(),
  applicationId: z.string().uuid(),
  productId: z.string().uuid(),
  amount: z.number().int().nonnegative(),
  method: PaymentMethodSchema.nullable().optional(),
  status: PaymentStatusSchema,
  paidAt: z.string().datetime().nullable().optional(),
  transactionId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Payment = z.infer<typeof PaymentSchema>;

// ============================================
// Multi-tenant Platform Types
// ============================================

export const TenantTypeSchema = z.enum(['national', 'public', 'private']);
export type TenantType = z.infer<typeof TenantTypeSchema>;

export const TenantStatusSchema = z.enum(['active', 'suspended', 'pending', 'provisioning']);
export type TenantStatus = z.infer<typeof TenantStatusSchema>;

export const TenantConfigSchema = z
  .object({
    branding: z
      .object({
        primaryColor: z.string().optional(),
        logoUrl: z.string().optional(),
      })
      .optional(),
    csvLayout: z.string().optional(),
    filePassword: z
      .object({
        enabled: z.boolean(),
        format: z.string().optional(),
      })
      .optional(),
  })
  .passthrough(); // Allow extra props

export const TenantSchema = z.object({
  id: z.string(),
  code: z.string(),
  ministryCode: z.string().optional().nullable(),
  type: TenantTypeSchema.optional(),
  name: z.string(),
  domain: z.string().nullable(),
  address: z.string().optional().nullable(),
  website: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),

  status: TenantStatusSchema,

  storageRegion: z.string().optional().nullable(),
  flywireEnabled: z.enum(['true', 'false']).optional().nullable(),
  ncueeLinkageEnabled: z.enum(['true', 'false']).optional().nullable(),
  jbcLinkageEnabled: z.enum(['true', 'false']).optional().nullable(),

  config: TenantConfigSchema.optional().nullable(),

  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Tenant = z.infer<typeof TenantSchema>;

/**
 * Students (Enrollment DB)
 */
export const StudentStatusSchema = z.enum(['ENROLLED', 'LEAVE', 'GRADUATED', 'WITHDRAWN']);
export type StudentStatus = z.infer<typeof StudentStatusSchema>;

export const StudentSchema = z.object({
  id: z.string().uuid(),
  studentNumber: z.string().min(1),
  applicantId: z.string().uuid().nullable().optional(),
  nameLast: z.string().min(1),
  nameFirst: z.string().min(1),
  nameKanaLast: z.string().nullable().optional(),
  nameKanaFirst: z.string().nullable().optional(),
  email: z.string().email(),
  structureId: z.string().uuid(),
  enrollmentYear: z.number().int(),
  status: StudentStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Student = z.infer<typeof StudentSchema>;

/**
 * Enrollments (Procedure flow)
 */
export const EnrollmentStepSchema = z.enum(['INFO_ENTRY', 'PAYMENT', 'DOCUMENT', 'COMPLETE']);
export type EnrollmentStep = z.infer<typeof EnrollmentStepSchema>;

export const EnrollmentSchema = z.object({
  id: z.string().uuid(),
  studentId: z.string().uuid(),
  step: EnrollmentStepSchema,
  completedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string(),
});
export type Enrollment = z.infer<typeof EnrollmentSchema>;

/**
 * Alumni
 */
export const AlumniSchema = z.object({
  id: z.string().uuid(),
  studentId: z.string().uuid().nullable().optional(),
  studentNumber: z.string().min(1),
  nameLast: z.string().min(1),
  nameFirst: z.string().min(1),
  email: z.string().email().nullable().optional(),
  graduationYear: z.number().int(),
  structureId: z.string().uuid(),
  degree: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type Alumni = z.infer<typeof AlumniSchema>;

// ============================================
// Content Management Types
// ============================================

export const AnnouncementTargetSchema = z.enum(['all', 'applicants', 'staff']);
export type AnnouncementTarget = z.infer<typeof AnnouncementTargetSchema>;

export const AnnouncementSchema = z.object({
  id: z.string(),
  title: z.string().min(1),
  body: z.string().min(1),
  target: AnnouncementTargetSchema.default('all'),
  isPublished: z.boolean().default(false),
  publishedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Announcement = z.infer<typeof AnnouncementSchema>;

export const DocumentSchema = z.object({
  id: z.string(),
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  fileKey: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  isPublic: z.boolean().default(false),
  createdAt: z.string(),
});
export type AppDocument = z.infer<typeof DocumentSchema>;

export const StaticPageSchema = z.object({
  code: z.string(),
  title: z.string().min(1),
  content: z.string(),
  locale: z.string().default('ja'),
  updatedAt: z.string(),
});
export type StaticPage = z.infer<typeof StaticPageSchema>;

// ============================================
// University Master / Search Types
// ============================================

export const UniversitySchema = z.object({
  ministryCode: z.string(), // 13-digit MEXT code
  name: z.string(),
  nameEn: z.string().optional(),
  type: z.enum(['national', 'public', 'private']),
  institutionType: z.string().default('university'),
  prefecture: z.string(),
  address: z.string(),
  code: z.string(), // URL slug / subdomain
  hasSystem: z.boolean(), // setup completed
  faculties: z.array(z.string()).default([]),
});

export type University = z.infer<typeof UniversitySchema>;

export const LabelSchema = z.object({
  key: z.string(),
  value: z.string(),
  description: z.string().nullable().optional(),
  updatedAt: z.string(),
});
export type Label = z.infer<typeof LabelSchema>;
